import { useEffect, useState } from 'react';
import { Stack, Card, Group, Text, Button, SimpleGrid } from '@repletoryreach/sdk-ui';
import { Alert, PageHeader } from '@repletoryreach/sdk-ui/data-display';
import { NumberInput, Switch, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useAffiliateSettings, useUpdateAffiliateSettings } from '../../../data/useAffiliates';
import { formatDateTime } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';

/**
 * The numbers the whole programme runs on.
 *
 * Two of them are money-safety rather than marketing. The clearing period is
 * how long a share is held before a partner can take it, and it exists so a
 * reversed payment can still be undone; setting it to zero pays out
 * immediately and carries the risk. The terms text is versioned rather than
 * edited in place: what a partner accepted is recorded on their row, so
 * changing the words has to move the number too.
 */
export default function PartnerSettingsPage() {
  const t = useT();
  const settings = useAffiliateSettings();
  const save = useUpdateAffiliateSettings();

  const [enabled, setEnabled] = useState(true);
  const [autoApprove, setAutoApprove] = useState(false);
  const [percent, setPercent] = useState<number | ''>('');
  const [months, setMonths] = useState<number | ''>('');
  const [clearance, setClearance] = useState<number | ''>('');
  const [minimum, setMinimum] = useState<number | ''>('');
  const [lateDays, setLateDays] = useState<number | ''>('');
  const [terms, setTerms] = useState('');

  const s = settings.data?.data;

  useEffect(() => {
    if (!s) return;
    setEnabled(s.programme_enabled);
    setAutoApprove(s.auto_approve);
    setPercent(s.default_percent);
    setMonths(s.default_months ?? '');
    setClearance(s.clearance_days);
    setMinimum(Math.round(s.min_withdrawal_minor) / 100);
    setLateDays(s.late_code_days);
    setTerms(s.terms_markdown);
  }, [s]);

  async function submit() {
    try {
      await save.mutateAsync({
        programme_enabled: enabled,
        auto_approve: autoApprove,
        default_percent: percent === '' ? undefined : percent,
        default_months: months === '' ? null : months,
        clear_months: months === '',
        clearance_days: clearance === '' ? undefined : clearance,
        min_withdrawal_minor: minimum === '' ? undefined : Math.round(minimum * 100),
        late_code_days: lateDays === '' ? undefined : lateDays,
        terms_markdown: terms
      });
      notifySuccess(t('partners.settings.saved'));
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('partners.settings.failed'));
    }
  }

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('partners.eyebrow')}
        title={t('partners.settings.title')}
        description={t('partners.settings.description')}
      />

      {settings.isError && (
        <Alert color="error" role="alert">
          {t('partners.settings.loadFailed')}{' '}
          <Button variant="subtle" size="sm" onClick={() => void settings.refetch()}>{t('action.retry')}</Button>
        </Alert>
      )}

      <Card withBorder>
        <Stack gap="md">
          <Switch
            label={t('partners.settings.enabled')}
            description={t('partners.settings.enabledHint')}
            checked={enabled}
            onChange={(v) => setEnabled(v)}
          />
          <Switch
            label={t('partners.settings.autoApprove')}
            description={t('partners.settings.autoApproveHint')}
            checked={autoApprove}
            onChange={(v) => setAutoApprove(v)}
          />

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <NumberInput
              label={t('partners.settings.percent')}
              description={t('partners.settings.percentHint')}
              value={percent}
              onChange={(v) => setPercent(typeof v === 'number' ? v : '')}
              min={0}
              max={100}
            />
            <NumberInput
              label={t('partners.settings.months')}
              description={t('partners.settings.monthsHint')}
              value={months}
              onChange={(v) => setMonths(typeof v === 'number' ? v : '')}
              min={1}
              max={120}
            />
            <NumberInput
              label={t('partners.settings.clearance')}
              description={t('partners.settings.clearanceHint')}
              value={clearance}
              onChange={(v) => setClearance(typeof v === 'number' ? v : '')}
              min={0}
              max={180}
            />
            <NumberInput
              label={t('partners.settings.minimum')}
              description={t('partners.settings.minimumHint')}
              value={minimum}
              onChange={(v) => setMinimum(typeof v === 'number' ? v : '')}
              min={0}
            />
            <NumberInput
              label={t('partners.settings.lateDays')}
              description={t('partners.settings.lateDaysHint')}
              value={lateDays}
              onChange={(v) => setLateDays(typeof v === 'number' ? v : '')}
              min={0}
              max={365}
            />
          </SimpleGrid>
        </Stack>
      </Card>

      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Stack gap="xs">
              <Text role="label">{t('partners.settings.terms')}</Text>
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('partners.settings.termsHint')}</Text>
            </Stack>
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('partners.settings.termsVersion', { version: String(s?.terms_version ?? 1) })}
            </Text>
          </Group>
          <Textarea value={terms} onChange={(e) => setTerms(e.currentTarget.value)} minRows={16} />
        </Stack>
      </Card>

      <Group justify="space-between">
        <Text role="caption" c="var(--rr-color-text-muted)">
          {s?.updated_at ? t('partners.settings.updated', { when: formatDateTime(s.updated_at) }) : ''}
        </Text>
        <Button onClick={submit} loading={save.isPending}>{t('action.save')}</Button>
      </Group>
    </Stack>
  );
}
